import { FC, RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { TooltipGroup } from '@/components/shared/tooltip/TooltipGroup';
import Text from '@/storybook/components/Text/Text';
import Button from '@/storybook/components/Button/Button';
import Alert from '@/storybook/components/Alert/Alert';
import LoadingSpinner from '@/storybook/components/LoadingSpinner/LoadingSpinner';
import type { ChatbotConversation } from '@/models/chatbot';
import type { ChatHistoryGroup } from './groupConversationsByDate';
import type { HistoryTimeLabels } from './formatHistoryTime';
import ChatHistoryListItem from './ChatHistoryListItem';
import useInfiniteScrollSentinel from './useInfiniteScrollSentinel';
import useResizeSignal from './useResizeSignal';
import { CHAT_HISTORY_TEST_IDS } from './testIds';
import styles from './ChatHistoryPanel.module.css';

interface ChatHistoryPagination {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onReachEnd: () => void;
  isError: boolean;
  errorMessage: string;
  onRetry: () => void;
}

interface ChatHistoryRenameControls {
  renamedId: string | null;
  onStart: (conversation: ChatbotConversation) => void;
  onAnimationEnd: (conversationId: string) => void;
}

interface ChatHistoryListProps {
  groups: ChatHistoryGroup[];
  timeLabels: HistoryTimeLabels;
  openingId: string | null;
  highlight?: string;
  selectedId?: string | null;
  onOpen: (conversationId: string) => void;
  onDelete: (conversation: ChatbotConversation) => void;
  pagination: ChatHistoryPagination;
  rename: ChatHistoryRenameControls;
  scrollRootRef?: RefObject<HTMLElement | null>;
}

const ChatHistoryList: FC<ChatHistoryListProps> = ({
  groups,
  timeLabels,
  openingId,
  highlight,
  selectedId,
  onOpen,
  onDelete,
  pagination,
  rename,
  scrollRootRef,
}) => {
  const { t } = useTranslation();
  const { hasNextPage, isFetchingNextPage, onReachEnd, isError: isLoadMoreError, errorMessage, onRetry } = pagination;
  const { renamedId, onStart, onAnimationEnd } = rename;

  const setSentinel = useInfiniteScrollSentinel({ onReachEnd, rootRef: scrollRootRef });
  const truncationRecheckSignal = useResizeSignal(scrollRootRef);

  return (
    <TooltipGroup id="chat-history-titles" tooltipClassName={styles.titleTooltip}>
      {groups.map(group => (
        <section key={group.id} className={styles.group}>
          <Text as="h3" className={styles.groupTitle}>
            {t(`components.aiChatDrawer.history.groups.${group.id}`)}
          </Text>
          <ul className={styles.itemList}>
            {group.conversations.map(conversation => (
              <ChatHistoryListItem
                key={conversation.id}
                conversation={conversation}
                timeLabels={timeLabels}
                isOpening={openingId === conversation.id}
                isSelected={conversation.id === selectedId}
                isRenaming={renamedId === conversation.id}
                highlight={highlight}
                truncationRecheckSignal={truncationRecheckSignal}
                onOpen={onOpen}
                onStartRename={onStart}
                onDelete={onDelete}
                onRenameAnimationEnd={onAnimationEnd}
              />
            ))}
          </ul>
        </section>
      ))}

      {isLoadMoreError ? (
        <Alert
          variant="error"
          className={styles.loadMoreAlert}
          message={errorMessage}
          data-testid={CHAT_HISTORY_TEST_IDS.loadMoreError}
        >
          <Button variant="text" size="S" onClick={onRetry} data-testid={CHAT_HISTORY_TEST_IDS.loadMoreErrorRetry}>
            {t('components.aiChatDrawer.history.loadMoreErrorRetry')}
          </Button>
        </Alert>
      ) : null}

      {hasNextPage && !isLoadMoreError ? (
        <div ref={setSentinel} className={styles.sentinel} data-testid={CHAT_HISTORY_TEST_IDS.sentinel}>
          {isFetchingNextPage ? (
            <div className={styles.loadingMore}>
              <LoadingSpinner hideLoadingMessage size={44} className={styles.spinner} />
            </div>
          ) : null}
        </div>
      ) : null}
    </TooltipGroup>
  );
};

export default ChatHistoryList;
